import {FormControl} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {
  GetArticleResponse,
  usePutArticleStockManagementCalculationMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {useDebouncedCallback} from 'shared/web';

import {ArticleDetailForm} from '../types/ArticleDetailFormSchema';
import {mapStockCalculationToForm} from '../utils/mapStockCalculationToForm';
import {CurrentStockForecastCards} from './CurrentStockForecastCards';
import {ForecastCard} from './ForecastCard';
import {GeneralSettingsCard} from './GeneralSettingsCard';

interface StockManagementTabProps {
  article: GetArticleResponse;
  control: FormControl<ArticleDetailForm>;
  formApi: UseFormReturn<ArticleDetailForm>;
  handlingUnitLabel: string;
}

export function StockManagementTab(props: StockManagementTabProps) {
  const {warehouseId, articleId} = useWarehouseParams();

  const [calculateStock] = usePutArticleStockManagementCalculationMutation({
    fixedCacheKey: 'stock-management-calculation',
  });

  const [stockManagement, setStockManagement] = useState<GetArticleResponse['stockManagement']>(
    props.article.stockManagement
  );

  const handleCalculateStock = useDebouncedCallback(() => {
    props.formApi.clearErrors();

    const stockManagementFormValues = props.formApi.getValues('stockManagement');

    const minQuantity = stockManagementFormValues?.stockManagementMinQuantity ?? 0;
    const maxQuantity = stockManagementFormValues?.stockManagementMaxQuantity ?? 0;

    const isMinQuantityGreaterThanZero = minQuantity > 0;
    const isMaxQuantityGreaterThanZero = maxQuantity > 0;

    if (!isMinQuantityGreaterThanZero) {
      props.formApi.setError('stockManagement.stockManagementMinQuantity', {
        message: i18n.t('general.errors.number.positive'),
      });
    }

    if (!isMaxQuantityGreaterThanZero) {
      props.formApi.setError('stockManagement.stockManagementMaxQuantity', {
        message: i18n.t('general.errors.number.positive'),
      });
    }

    if (!isMinQuantityGreaterThanZero || !isMaxQuantityGreaterThanZero) {
      return;
    }

    const isMinQuantityValid = minQuantity <= maxQuantity;
    const isMaxQuantityValid = maxQuantity >= minQuantity;

    if (!isMinQuantityValid) {
      props.formApi.setError('stockManagement.stockManagementMinQuantity', {
        message: i18n.t('general.errors.number.lowerOrEqual', {max: maxQuantity}),
      });
    }

    if (!isMaxQuantityValid) {
      props.formApi.setError('stockManagement.stockManagementMaxQuantity', {
        message: i18n.t('general.errors.number.greaterOrEqual', {min: minQuantity}),
      });
    }

    if (!isMinQuantityValid || !isMaxQuantityValid) {
      return;
    }

    calculateStock({
      warehouseId,
      articleId,
      body: {
        minQuantity,
        maxQuantity,
      },
    })
      .unwrap()
      .then((result) => {
        const mappedValues = mapStockCalculationToForm(result);

        setStockManagement((prev) => ({
          ...prev,
          ...mappedValues,
        }));

        props.formApi.setValue('stockManagement', {
          ...stockManagementFormValues,
          ...mappedValues,
        });
      })
      .catch(handleApiError);
  }, RECALCULATION_DELAY);

  return (
    <VStack spacing={4} width="100%">
      <CurrentStockForecastCards
        stockManagement={stockManagement}
        handlingUnitLabel={props.handlingUnitLabel}
      />
      <GeneralSettingsCard
        stockManagement={stockManagement}
        control={props.control}
        formApi={props.formApi}
        handlingUnitLabel={props.handlingUnitLabel}
        onChange={handleCalculateStock}
      />
      <ForecastCard stockManagement={stockManagement} handlingUnitLabel={props.handlingUnitLabel} />
    </VStack>
  );
}

const RECALCULATION_DELAY = 500;
